import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Form, Header, Message, Segment } from 'semantic-ui-react';
import SurveyService from '~/api/services/survey';
import notify from '~/components/Notify';
import { StyledAnswer, StyledSegmentWrapper, StyledWrapper } from './style';
import Layout from '~/components/Layout';
import { RouteTypes } from '~/routes/constants';
import { StyledRating } from '../ShowSurvey/CreateEditQuestion/QuestionForms/Stars';
import { formatNumberWithoutFractions, formatNumbers, handleErrors } from '~/utils/helpers';
import { MaxValueLabel, MinValueLabel, SliderC, SliderWrapper } from '../ShowSurvey/CreateEditQuestion/QuestionForms/Slider';
import { StyledGroupQuestion } from '../ShowSurvey/CreateEditQuestion/style';

const AnswersPreview: React.FC = () => {
  const { t } = useTranslation()
  const { idSurvey, id } = useParams()

  const [isBusy, setIsBusy] = useState(false)
  const [questions, setQuestions] = useState(null)
  const [answers, setAnswers] = useState(null)

  useEffect(() => {
    fetchQuestionsAndAnswers()
  }, [])

  async function fetchQuestionsAndAnswers() {
    setIsBusy(true)
    try {
      let answersResponse
      let questionsResponse
      if (!id) {
        answersResponse = await SurveyService.editSurveysQuestions(idSurvey)
        questionsResponse = await SurveyService.getFarmerQuestions(idSurvey, { page: 0, rows: 100 })
      } else {
        answersResponse = await SurveyService.getFarmerAnswers(idSurvey, id)
        questionsResponse = await SurveyService.getSurveyQuestions(idSurvey)
      }
      setQuestions(questionsResponse.data)
      setAnswers(answersResponse.data)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderAnswer(question, answer, answers = []) {
    switch (question?.type) {
      case 'text':
        return answer?.customText || t('Not answered')
      case 'radio':
        return question?.answers?.map(answerOption => {
          return <Form.Checkbox
            radio
            label={answerOption.answer}
            checked={answerOption.id === answer?.answerId}
            disabled
            key={answerOption.id}
          />
        })
      case 'select':
        return <Form.Dropdown
          selection
          width={8}
          label={t('Selected answers')}
          options={question?.answers?.map(answerOption => ({ text: answerOption.answer, value: answerOption.id }))}
          value={answer?.answerId}
          disabled
          required={Boolean(question?.required)}
        />
      case 'stars':
        return <>
          <StyledRating
            icon='star'
            defaultRating={answer?.value}
            maxRating={question?.value}
            disabled
            size={'large'}
          />
        </>
      case 'number':
        return <Form.Input
          width={8}
          label={
            t('Min - Max: {{min}} {{unit}} - {{max}} {{unit}}',
              { min: formatNumbers(question.minValue, question?.subtype), max: formatNumbers(question.maxValue, question?.subtype), unit: question?.unitLabel })
          }
          value={formatNumbers(answer?.value, question?.subtype)}
          placeholder={question?.placeholder}
        />
      case 'slider':
        const sliderValue = answer?.value || 0
        return <Form.Field>
          <Form.Field label={t('Rating: {{rating}} {{watchUnit}}',
            { rating: formatNumberWithoutFractions(parseInt(sliderValue)), watchUnit: question?.unitLabel })} />
          <SliderWrapper>
            <MinValueLabel>{`${formatNumberWithoutFractions(question?.minValue)} ${question?.unitLabel || ''}`}</MinValueLabel>
            <SliderC
              rating={((parseInt(sliderValue) - question?.minValue) / (question?.maxValue - question?.minValue)) * 100}
              step={parseInt(question?.interval)}
              disabled
              min={question?.minValue}
              max={question?.maxValue}
              value={parseInt(sliderValue)}
            />
            <MaxValueLabel>{`${formatNumberWithoutFractions(question?.maxValue)} ${question?.unitLabel || ''}`}</MaxValueLabel>
          </SliderWrapper>
        </Form.Field>
      case 'sorting':
        return <Form.Field required={Boolean(question?.required)} >
          {answers?.map((answer, index) => {
            const answerText = question?.answers?.find(answerQuestion => answerQuestion.id === answer.answerId)?.answer || ''
            return <StyledAnswer key={answer?.id}>
              {`${index + 1}. ${answerText}`}
            </StyledAnswer>
          })}
        </Form.Field>
      case 'multiselect':
        return <Form.Dropdown
          selection
          width={8}
          multiple
          label={t('Selected answers')}
          options={question?.answers?.map(answerOption => ({ text: answerOption.answer, value: answerOption.id }))}
          value={answers.map(answer => answer?.answerId)}
          disabled
          required={Boolean(question?.required)}
        />
      case 'group':
        return <>
          <Form.Field label={question?.question} required={Boolean(question?.required)} />
          {question.groupQuestions?.map((groupQuestion) => {
            return <div key={groupQuestion.id} style={{ display: 'flex' }}>
              <StyledGroupQuestion
                label={groupQuestion.question}
              />
              <Form.Group widths={'equal'}>
                {question?.answers?.map((answer) => {
                  const checkedAnswers = answers.find(answerGroup => answerGroup.id === groupQuestion.id)
                  return <Form.Field key={answer?.id} style={{ textAlign: 'center' }}>
                    <div className='radio-container'>
                      <label>{answer.answer}</label>
                      <Form.Checkbox
                        disabled
                        radio
                        checked={checkedAnswers?.answers[0].answerId === answer?.id}
                        style={{ marginTop: 3 }}
                      />
                    </div>
                  </Form.Field>
                })}
              </Form.Group>
            </div>
          })}
        </>
      case 'multitext':
        return <>
          <Form.Field label={question?.question} required={Boolean(question?.required)} />
          {answers.map((answer, index) => {
            return <Form.TextArea
              value={answer.customText}
              onChange={(e) => e.preventDefault()}
              style={{ cursor: 'default' }}
              onClick={(e) => e.preventDefault()}
              key={index}
            />
          })}
        </>
      case 'checkbox':
        return question?.answers?.map(answerOption => {
          return <Form.Checkbox
            label={answerOption.answer}
            checked={Boolean(answers?.find(answer => answer?.answerId === answerOption?.id))}
            disabled
            key={answerOption.id}
          />
        })
      default:
        return
    }
  }

  function renderNoAnswer() {
    return <Message style={{ width: 'fit-content', margin: 'auto' }}>
      <Message.Header>{t('No results')}</Message.Header>
      <p>{t('Sorry, there are no answers to display.')}</p>
    </Message>
  }

  function renderTextQuestion(question, index) {
    const answer = answers?.find(answer => answer.id === question.id)?.answers[0]
    const answerKey = question.type === 'group' ? 'groupQuestions' : 'answers'
    const answersArrayValue = answers?.find(answer => answer.id === question.id)
    return <StyledSegmentWrapper>
      <Header attached='top' block style={{ margin: 0 }}>
        <div className='title'>{`${index + 1}. ${question?.question}`}</div>
        <div>{question?.typeLabel}</div>
      </Header>
      <Segment attached>
        <Form style={{ margin: 20, width: '100%' }}>
          {!answer ? renderNoAnswer() : renderAnswer(question, answer, answersArrayValue ? answersArrayValue[answerKey] : [])}
        </Form>
      </Segment>
    </StyledSegmentWrapper>
  }

  function renderContent() {
    return <>
      {questions?.map((question, index) => {
        return <div key={question.id}>{renderTextQuestion(question, index)}</div>
      })}
    </>
  }

  function renderNoResultsMessage() {
    return <Message info>
      <Message.Header>{t('No results')}</Message.Header>
      <p>{t('Sorry, there are no answers to display.')}</p>
    </Message>
  }

  return <Layout
    isBusy={isBusy}
    segmented
    close={id ? RouteTypes.SURVEY_SHOW_RESUTLS.replace(':id', idSurvey) : RouteTypes.SURVEY}
    title={t('Answers')}
  >
    <StyledWrapper>
      {answers && renderContent()}
      {!isBusy && questions?.length === 0 && renderNoResultsMessage()}
    </StyledWrapper>
  </Layout>
}
export default AnswersPreview